/**
 * @fileOverview Seals the Erklärung's embedded app instance off from the reader's own browser state.
 * Imported **first** in `main.js`, so it runs before any other module has read or written anything.
 * See docs/view-help.md, "The embedded instance".
 *
 * The frame is the real app on the reader's own origin. The sandbox (`sandbox.ts`) keeps its requests
 * off the reader's data on the server — but the browser is shared too: `localStorage`,
 * `sessionStorage`, cookies and `BroadcastChannel` are one per origin, so the frame was writing into
 * the very storage the reader's session lives in. Guarding each feature's writes one by one missed
 * the ones nobody thought of: a 401 on a pass-through read ran the app's logout inside the frame,
 * which blanked the JWT and the recent projects in the reader's own tab; the layout, the locale, the
 * cost page's choices and the router's reload guard were written from sample state.
 *
 * So the instance gets its own storage, wholesale, instead of a list of guards:
 *
 * - **`localStorage` and `sessionStorage`** are replaced by in-memory stores. `localStorage` is seeded
 *   with **only** the keys the instance needs to be the reader's session at all — the token, the
 *   workspace, the locale and the theme ({@link SHARED_LOCAL_KEYS}) — and `sessionStorage` with
 *   nothing. Every write stays in the frame's memory and is gone with it.
 *
 *   Only those keys, and not a copy of everything: a full copy opened the frame on the reader's own
 *   remembered choices — a collapsed drawer, a zoom level, a cost page's valuation, a project
 *   selection that intersects the sample company to nothing — and every feature that remembers
 *   something needed a guard of its own to ignore it, which the next such feature would forget. A
 *   frame that starts as a first visit needs none.
 * - **Cookies** can be read, never written.
 * - **`BroadcastChannel`** reaches nothing: a channel would carry the sample world into the reader's
 *   other tabs, and theirs into the frame.
 * - **`window.open`** opens nothing: a popout would be a new, unsealed window of the app.
 * - **`navigator.sendBeacon`** sends nothing: it is a write that bypasses both `apiClient` and the
 *   network guard.
 */

import { IS_VIEW_HELP_EMBED, VIEW_HELP_SEALED_MARK } from "@/services/viewHelp/embed"

/**
 * The `localStorage` keys the frame is given a copy of: what signs it in as the reader
 * (`store/authentication.js`: `token`, `TENANT`) and what makes it look like their app
 * (`plugins/i18n`: `locale`, `useThemePreference`: `theme`). Spelled out rather than imported, because
 * this module runs before any other has loaded. Keep in sync with those.
 */
export const SHARED_LOCAL_KEYS = ["token", "TENANT", "locale", "theme"]

/** A `Storage` held in memory, starting from a copy of `keys` in `source`. */
function memoryStorage(source: Storage | null, keys: string[]): Storage {
  const entries = new Map<string, string>()
  try {
    for (const key of keys) {
      const value = source?.getItem(key) ?? null
      if (value !== null) entries.set(key, value)
    }
  } catch {
    // Storage the browser refuses to read (a privacy mode) leaves the copy empty; the frame then simply
    // behaves like a first visit, which is still separate.
  }

  const storage = {
    get length() {
      return entries.size
    },
    key: (index: number) => [...entries.keys()][index] ?? null,
    getItem: (key: string) => entries.get(String(key)) ?? null,
    setItem: (key: string, value: string) => void entries.set(String(key), String(value)),
    removeItem: (key: string) => void entries.delete(String(key)),
    clear: () => entries.clear(),
  }
  return storage as Storage
}

/** Replaces one of the window's storages by a memory copy. */
function isolateStorage(win: Window, name: "localStorage" | "sessionStorage", keys: string[]) {
  let source: Storage | null = null
  try {
    source = win[name]
  } catch {
    source = null
  }
  const copy = memoryStorage(source, keys)
  Object.defineProperty(win, name, { configurable: true, get: () => copy })
}

/** A `BroadcastChannel` that neither sends nor receives. */
class SilentChannel extends EventTarget {
  readonly name: string
  onmessage: ((_event: MessageEvent) => void) | null = null
  onmessageerror: ((_event: MessageEvent) => void) | null = null

  constructor(name: string) {
    super()
    this.name = name
  }

  postMessage() {}

  close() {}
}

/** Cuts every tie between `win`'s app instance and the reader's browser state. */
export function isolateEmbeddedInstance(win: Window & typeof globalThis) {
  isolateStorage(win, "localStorage", SHARED_LOCAL_KEYS)
  // Nothing: the reader's reload guard and workspace-switch address describe *their* tab, not the frame.
  isolateStorage(win, "sessionStorage", [])

  const cookie = Object.getOwnPropertyDescriptor(win.Document.prototype, "cookie")
  if (cookie?.get) {
    Object.defineProperty(win.document, "cookie", {
      configurable: true,
      get: () => cookie.get!.call(win.document),
      set: () => {},
    })
  }

  Object.defineProperty(win, "BroadcastChannel", { configurable: true, writable: true, value: SilentChannel })
  win.open = () => null
  if (typeof win.navigator.sendBeacon === "function") {
    Object.defineProperty(win.navigator, "sendBeacon", { configurable: true, value: () => false })
  }

  // Last, so the mark means all of the above happened. The player drives no window without it.
  Object.defineProperty(win, VIEW_HELP_SEALED_MARK, { value: true })
}

if (IS_VIEW_HELP_EMBED && typeof window !== "undefined") isolateEmbeddedInstance(window)
